import { ObjectState } from "@homebound/form-state";
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { buildInsideMapsData, InsideMapsData } from "src/components/molecules/InsideMaps/insideMapsData";
import { useModal } from "src/contexts";
import {
  ToggleOption_CheckoutConfigOptionGroupFragment,
  ToggleOption_CheckoutConfigPotentialOptionFragment,
} from "src/fragments/circularFragments.generated";
import {
  createCommunitiesUrl,
  createConfigurationsLandingUrl,
  createConfiguratorUrl,
  createLotsUrl,
  createPlanSelectionUrl,
} from "src/RouteUrls";
import { startSaveVital } from "src/utils/analytics";
import { useCurrentUser } from "src/utils/hooks/useCurrentUser";
import { fetchAndCacheSVG } from "~components";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { getDebugParts, isDefined, pluralize } from "~utils";
import { calcSelectedOptionIds } from "./calcSelectedOptionIds";
import { WarningModal } from "./components";
import {
  ConfiguratorContext_CheckoutConfigFragment,
  useConfiguratorContext_LoadConfigQuery,
} from "./Configurator.context.generated";
import { getFloorPlanImages, shouldShowPriceLoading } from "./Configurator.utils";
import {
  ProductDetails_CheckoutConfigPotentialOptionFragment,
  useProductDetails_LoadQuery,
} from "./OptionProductDetails.generated";
import { sortOptionGroups } from "./sortOptionGroups";
import { FlowStep, FlowSteps, Step, useStepFromLocation } from "./stepTemplates/FlowSteps";
import { CheckoutFormValue, useCheckoutFormState } from "./useCheckoutFormState";
import { usePlanChangePolling } from "./usePlanChangePolling";

/** Configurator Context **/
export type ConfiguratorValues = {
  communityId: string | null;
  lotId: string | null;
  // this should be as minimal as possible, and each step should use its own fragments
  checkoutConfig: ConfiguratorContext_CheckoutConfigFragment | undefined;
  formState: ObjectState<CheckoutFormValue>;
  selectedOptionIds: string[];
  setOptionIds: (optionIds: string[]) => void;
  autoSaving: boolean;
  currentStep: Step;
  currentFlowStep: FlowStep;
  currentSubStep: number;
  hasAduSupport: boolean;
  /**
   * We need to track the last changed group ID to determine if the price loading state should be shown for a specific option.
   */
  lastChangedGroupId: string | null;
  insideMaps: InsideMapsData;
  /**
   * Whether the product details (materialVariant, displayProducts, primaryProduct) are still loading.
   * Cards can use this to show a loading state for product-specific UI elements while still
   * displaying the option name and pricing from the initial context query.
   */
  productDetailsLoading: boolean;
  /**
   * Get product details (displayProducts, primaryProduct, materialVariant) for an option by ID.
   * Returns undefined if the option doesn't have product details or they haven't loaded yet.
   */
  getProductDetails: (optionId: string) => ProductDetails_CheckoutConfigPotentialOptionFragment | undefined;
  /**
   * Plan selection is now local state that only persists on "Next", so this flag is hoisted
   * into the context to prevent navigation while the form is dirty.
   */
  hasDirtyPlanSelection: boolean;
};

/** Configurator Actions **/
export type ConfiguratorActions = {
  toggleOption: (
    group: ToggleOption_CheckoutConfigOptionGroupFragment,
    option: ToggleOption_CheckoutConfigPotentialOptionFragment,
  ) => void;
  // Note: _updateConfiguration, goToNextStep, and goToStep could be combined depending on how we want to handle
  // our calls. Will save that for future PRs as we start fleshing out steps
  goToStep: (stepTemplate: FlowStep, subStep?: string) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  // Before getting into a config, we need to set the community, then the lot
  setCommunityId: (communityId: string) => void;
  setLotId: (lotId: string) => void;
  // Determines if the price loading state should be shown for a specific option.
  showPriceLoadingState: (optionId: string | undefined) => boolean;
  setHasDirtyPlanSelection: (dirty: boolean) => void;
};

export type ConfiguratorContext = ConfiguratorValues & ConfiguratorActions;
const ConfiguratorContext = createContext<ConfiguratorContext | null>(null);
export type GoToStep = Pick<ConfiguratorContext, "goToStep">;

type ConfiguratorProviderProps = {
  children: ReactNode;
};

export function ConfiguratorProvider(props: ConfiguratorProviderProps) {
  const { children } = props;
  const { currentUser, isHomeowner } = useCurrentUser();
  const { communityId: communityIdParam, ccfgId, structureId } = useParams();
  const { showModal, closeModal } = useModal();
  // Options and Interior have sub steps we need to track to allow context to control navigation
  const { subStep: urlSubStep } = useParams<{ subStep?: string }>();
  const currentSubStep = useMemo(() => parseInt(urlSubStep ?? "1") ?? 1, [urlSubStep]);

  const [communityId, setCommunityId] = useState<string | null>(communityIdParam ?? null);
  const [lotId, setLotId] = useState<string | null>(structureId ?? null);
  const [lastChangedGroupId, setLastChangedGroupId] = useState<string | null>(null);
  const [hasDirtyPlanSelection, setHasDirtyPlanSelection] = useState(false);

  // The first time we get data from the server, we establish a map of rpo -> order,
  // so that we can re-apply that stable order to newly-priced/updated options as the
  // config changes w/o having entries jump order as selections/prices change.
  const [stableOrderMap, setStableOrderMap] = useState<Map<string, number>>(new Map());

  function rebuildStableOrder(optionGroups: ConfiguratorContext_CheckoutConfigFragment["optionGroups"]) {
    const sorted = sortOptionGroups(optionGroups);
    const map = sorted.flatMap((ccfog, i) => {
      return [[ccfog.id, i] as const, ...ccfog.options.map((ccfo, j) => [ccfo.id, j] as const)];
    });
    setStableOrderMap(new Map(map));
  }

  const query = useConfiguratorContext_LoadConfigQuery({
    variables: { id: ccfgId ?? "", debugParts: getDebugParts() },
    skip: !ccfgId,
    // Override the global network-only default so that when PlanSelection seeds the cache
    // via cache.writeQuery before navigating, this query resolves instantly from cache
    // instead of firing a redundant network request. On cold loads (refresh/deep link) the
    // cache is empty so this still goes to the network. Freshness after that is handled
    // by mutation responses and refetch() calls (e.g. plan change polling).
    fetchPolicy: "cache-first",
    // We need to set this to true so that onCompleted gets called upon query.refetch
    // (which we call/only happens when the ccfg's structure changes, and we do a full reset)
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      // Snapshot our stable order
      rebuildStableOrder(data.checkoutConfig.optionGroups ?? []);

      // Preload floor plan SVGs early to improve load times
      const floorPlanImages = getFloorPlanImages(data.checkoutConfig.productOffering, data.checkoutConfig.isReversed);
      floorPlanImages.forEach((image) => {
        if (image.kind === "floorPlan" && image.imageUrl) {
          void fetchAndCacheSVG(image.imageUrl);
        }
      });
    },
  });

  // Always sort the latest data with the latest map
  const sortedOptionGroups = useMemo(() => {
    return (query.data?.checkoutConfig.optionGroups ?? [])
      .sortBy((ccfog) => stableOrderMap.get(ccfog.id) ?? 0)
      .map((ccfog) => {
        return {
          ...ccfog,
          options: ccfog.options.sortBy((ccfo) => stableOrderMap.get(ccfo.id) ?? 0),
        };
      });
  }, [query, stableOrderMap]);

  // Check if the plan has ADU support by traversing received OG
  const { hasAduSupport, navigableStepKeys } = useMemo(() => {
    const hasLegacyAduOGs = sortedOptionGroups.some((ccog) => ccog.step === CheckoutConfigOptionGroupStep.Adu);
    const hasNewAduSupport = (query.data?.checkoutConfig?.buildableAduPlans ?? []).nonEmpty;
    const hasAduSupport = hasLegacyAduOGs || hasNewAduSupport;
    const sortedStepKeys = Object.values(FlowStep).sort((a, b) => FlowSteps[a].sortOrder - FlowSteps[b].sortOrder);
    return {
      hasAduSupport,
      navigableStepKeys: hasAduSupport ? sortedStepKeys : sortedStepKeys.filter((step) => step !== FlowStep.Adu),
    };
  }, [sortedOptionGroups, query.data?.checkoutConfig?.buildableAduPlans]);

  const checkoutConfig = query.data?.checkoutConfig;

  // Filter option groups that need product details from the secondary query.
  // Only include groups with a personalizationType (SINGLE_PRODUCT, LINKED_PRODUCT, PACKAGE).
  // Plain add-ons without materials don't have a personalizationType and don't need product details.
  // Sort IDs to ensure consistent ordering and prevent unnecessary query re-executions.
  const rpogIds = useMemo(
    () =>
      sortedOptionGroups
        .filter((ccog) => ccog.group.personalizationType !== null)
        .map((ccog) => ccog.group.id)
        .sort(),
    [sortedOptionGroups],
  );

  // Secondary query to load product-level details (materialVariant, displayProducts, primaryProduct, etc.)
  // This enables a faster initial paint showing option names and pricing, with product details loading async.
  const productDetailsQuery = useProductDetails_LoadQuery({
    variables: {
      ccfgId: ccfgId ?? "",
      rpogIds,
    },
    // Only run once we have the main config and option groups loaded
    skip: !ccfgId || !checkoutConfig || rpogIds.length === 0,
  });

  // Build a map of option id -> product details from the secondary query
  const productDetailsMap = useMemo(() => {
    const map = new Map<string, ProductDetails_CheckoutConfigPotentialOptionFragment>();
    productDetailsQuery.data?.checkoutConfig.optionGroups.forEach((group) => {
      group.options.forEach((option) => {
        map.set(option.id, option);
      });
    });
    return map;
  }, [productDetailsQuery.data]);

  // Getter for components to access product details directly
  const getProductDetails = useCallback((optionId: string) => productDetailsMap.get(optionId), [productDetailsMap]);

  const { formState, autoSaving, setOptionIds, selectedOptionIds } = useCheckoutFormState(
    checkoutConfig,
    query.refetch,
    rebuildStableOrder,
  );

  // Interior step sub step count after "packages"
  const roomSubSteps = checkoutConfig?.interiorSubSteps ?? [];
  // TODO: Move these hooks out of context provider. THey will cause re-renders and realistically the child stepper
  //   should be the one that knows the steps and handles the switch statement (ie. <ConfiguratorStep />)
  const navigate = useNavigate();
  const currentStep = useStepFromLocation();

  // Poll for plan changes to detect when another tab/user has changed the product offering
  usePlanChangePolling({ checkoutConfig, onPlanChange: query.refetch });

  // Use the first room location id as our default as these steps are already ordered by the `insideMapsRoomOrder` field
  const defaultInsideMapsRoomLocationId = roomSubSteps.find(({ location }) => isDefined(location?.location.id))
    ?.location?.location.id;

  const insideMaps = useMemo(
    () => buildInsideMapsData(checkoutConfig?.insideMapsTextureSwaps, defaultInsideMapsRoomLocationId),
    [checkoutConfig?.insideMapsTextureSwaps, defaultInsideMapsRoomLocationId],
  );

  // Assume this is the ccfgId doesn't exist
  if (ccfgId && query && query.error) {
    console.error(query.error);
    navigate(createCommunitiesUrl());
  }

  // If currentUser is homeowner and this ccfg is not theirs then navigate back to landing page
  if (checkoutConfig && isHomeowner && !checkoutConfig.homeowners?.some((h) => h.id === currentUser?.id)) {
    navigate(createConfigurationsLandingUrl());
  }

  // Throw when there are more than one context
  if (useContext(ConfiguratorContext) !== null)
    throw new Error("ConfiguratorProvider should not be a child of another ConfiguratorProvider");

  // -------Navigation handlers --------
  // Note: We're saving the configuration upon each selection, so we shouldn't need to make any calls between steps
  function goToNextStep() {
    const index = navigableStepKeys.indexOf(currentStep as FlowStep);
    if (index < navigableStepKeys.length - 1) {
      const nextStep = navigableStepKeys[index + 1];
      if (currentStep === FlowStep.Interior) {
        handleInteriorNextStep(nextStep);
      } else {
        goToStep(nextStep, undefined);
      }
    }
  }

  function handleInteriorNextStep(nextStep: FlowStep) {
    if (currentSubStep < roomSubSteps.length) {
      goToStep(currentStep, `${currentSubStep + 1}`);
    } else {
      goToStep(nextStep, undefined);
    }
  }

  function goToPreviousStep() {
    const index = navigableStepKeys.indexOf(currentStep as FlowStep);
    if (index) {
      const previousStep = navigableStepKeys[index - 1];

      if (currentStep === FlowStep.Interior) {
        handleInteriorPrevStep(previousStep);
      } else if (currentStep === FlowStep.Options) {
        goToStep(previousStep, `${roomSubSteps.length}`);
      } else {
        goToStep(previousStep, undefined);
      }
    }
  }

  function handleInteriorPrevStep(previousStep: FlowStep) {
    if (currentSubStep > 1) {
      goToStep(currentStep, `${currentSubStep - 1}`);
    } else {
      // Jump to the last substep of the previous step
      goToStep(previousStep, "2");
    }
  }

  function goToStep(step: FlowStep, subStep?: string) {
    // Clear dirty plan state on any navigation — the Plan step sets this when
    // the user has uncommitted local changes, but once they navigate away it's moot.
    setHasDirtyPlanSelection(false);
    if (step === FlowStep.Lot && communityId) {
      return navigate(createLotsUrl(communityId));
    } else if (step === FlowStep.Community) {
      return navigate(createCommunitiesUrl());
    } else if (step === FlowStep.Plans && communityId && lotId && !ccfgId) {
      return navigate(createPlanSelectionUrl(communityId, lotId));
    } else if (communityId && ccfgId) {
      return navigate(createConfiguratorUrl(communityId, ccfgId, step, subStep));
    } else {
      return navigate(createCommunitiesUrl());
    }
  }
  // ------- End Navigation handlers --------

  function showRemovedOptionsConfirmationModal(
    removedOptions: { displayName: string; id: string }[],
    selectedOptionName: string,
    newOptionIds: string[],
    isRemovingOption: boolean,
  ) {
    const actionText = isRemovingOption ? "Removing" : "Selecting";
    const message = `${actionText} this ${selectedOptionName} will deselect the ${removedOptions.map((ro) => ro.displayName).join(", ")} ${pluralize(removedOptions.length, "option")} as they are conflicting.`;
    showModal(
      <WarningModal
        onClose={closeModal}
        onContinue={() => {
          startSaveVital();
          setOptionIds(newOptionIds);
          closeModal();
        }}
        title="Option Conflict"
        confirmationMessage={message}
        actionButtonText={isRemovingOption ? "REMOVE" : "SELECT"}
      />,
    );
  }

  async function toggleOption(
    group: ToggleOption_CheckoutConfigOptionGroupFragment,
    option: ToggleOption_CheckoutConfigPotentialOptionFragment,
  ) {
    const isAlreadySelected = selectedOptionIds.includes(option.option.id);
    const { newOptionIds, removedOptions } = calcSelectedOptionIds(
      checkoutConfig?.optionGroups ?? [],
      selectedOptionIds,
      group,
      option,
    );
    // Store the last changed group ID
    setLastChangedGroupId(group.group.id);

    // Skip over the modal if all removedOptions are personalization options that required group to be selected
    const shouldSkipModal = removedOptions.every((ro) =>
      group.group.groupPrereqChildren.some(({ options }) => options.some((o) => o.id === ro.id)),
    );

    // Show confirmation modal before applying changes to prevent unwanted option removals.
    // Users should be allows to deselect and have a chance to cancel before we mutate their configuration.
    if (removedOptions.nonEmpty && !shouldSkipModal) {
      showRemovedOptionsConfirmationModal(removedOptions, option.option.displayName, newOptionIds, isAlreadySelected);
    } else {
      startSaveVital();
      setOptionIds(newOptionIds);
    }
  }

  function showPriceLoadingState(optionId: string | undefined): boolean {
    return shouldShowPriceLoading(autoSaving, checkoutConfig?.optionGroups, optionId, lastChangedGroupId);
  }

  return (
    <ConfiguratorContext.Provider
      value={{
        // values
        currentStep: FlowSteps[currentStep!],
        currentFlowStep: currentStep,
        currentSubStep,
        communityId,
        lotId,
        formState,
        selectedOptionIds,
        setOptionIds,
        autoSaving,
        hasAduSupport,
        // Spread in the sorted option groups to override the original optionGroups
        checkoutConfig: checkoutConfig && {
          ...checkoutConfig,
          optionGroups: sortedOptionGroups,
        },
        lastChangedGroupId,
        hasDirtyPlanSelection,
        // Product details (displayProducts, primaryProduct, materialVariant) are loaded in a secondary query
        productDetailsLoading: productDetailsQuery.loading,
        getProductDetails,

        // Actions
        goToStep,
        goToNextStep,
        goToPreviousStep,
        toggleOption,
        setCommunityId,
        setLotId,
        showPriceLoadingState,
        setHasDirtyPlanSelection,
        insideMaps,
      }}
    >
      {children}
    </ConfiguratorContext.Provider>
  );
}

/** Configurator hook. **/
export function useConfiguratorContext() {
  const context = useContext(ConfiguratorContext);
  if (context === null) throw new Error("useConfiguratorContext must be used within a ConfiguratorProvider");
  return context;
}
